'use client';

import { useEffect, type RefObject } from 'react';

/**
 * Make everything outside a modal dialog `inert` while it is open, so Tab,
 * screen-reader virtual cursors and pointer clicks cannot reach the page
 * behind it (WCAG 2.4.3 focus order, 1.3.2; APG modal dialog pattern).
 *
 * Walks from the dialog up to <body> and marks every sibling at each level
 * inert, so it works for portaled dialogs (direct children of <body>) and for
 * dialogs rendered in place. Elements that were already inert are left alone
 * on cleanup.
 *
 * Making the page inert blurs whatever opened the dialog, so the hook records
 * the focused element first and moves focus back to it once the page is
 * interactive again. Call it BEFORE any effect that moves focus into the
 * dialog, so this effect sees the opener.
 */
export function useInertBackground(
  ref: RefObject<HTMLElement | null>,
  active = true,
): void {
  useEffect(() => {
    const dialog = ref.current;
    if (!active || !dialog) return;

    const opener =
      document.activeElement instanceof HTMLElement && !dialog.contains(document.activeElement)
        ? document.activeElement
        : null;
    const touched: HTMLElement[] = [];
    let node: HTMLElement | null = dialog;
    while (node && node !== document.body && node.parentElement) {
      for (const sibling of Array.from(node.parentElement.children)) {
        if (sibling === node || !(sibling instanceof HTMLElement)) continue;
        if (sibling.inert || sibling.tagName === 'SCRIPT') continue;
        sibling.inert = true;
        touched.push(sibling);
      }
      node = node.parentElement;
    }

    return () => {
      for (const el of touched) el.inert = false;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [ref, active]);
}
